import React from 'react';
import LegalPage from './LegalPage';

export default function SecurityPage() {
  return (
    <LegalPage title="Security" lastUpdated="April 30, 2026">
      <h2>1. Overview</h2>
      <p>
        Tooling Management Group LLC (&ldquo;TMG&rdquo;) takes the security of the ToolSight&trade; platform and the data it processes seriously. This Security Overview provides a summary of the security practices and controls we implement to protect the Service and the information entrusted to us by our clients.
      </p>
      <p>
        For complete details on TMG&rsquo;s security program, qualified parties may request a copy of our Information Security Policy by contacting us using the information in Section 11 below.
      </p>

      <h2>2. Infrastructure and Hosting</h2>
      <p>
        The ToolSight platform is hosted on Microsoft Azure, a leading enterprise cloud platform with comprehensive security certifications:
      </p>
      <ul>
        <li><strong>Cloud provider:</strong> Microsoft Azure (SOC 2 compliant, ISO 27001 certified, FIPS 140-2 compliant)</li>
        <li><strong>Data residency:</strong> All data is stored and processed within the United States (Azure East US and North Central US regions)</li>
        <li><strong>Database:</strong> Azure Cosmos DB with continuous backup and point-in-time restore</li>
        <li><strong>Media storage:</strong> Azure Blob Storage with geo-redundant replication</li>
        <li><strong>API layer:</strong> Azure Functions (serverless) &mdash; all data access routed through authenticated API endpoints</li>
        <li><strong>Web hosting:</strong> Azure Static Web Apps with edge distribution</li>
      </ul>

      <h2>3. Encryption</h2>
      <ul>
        <li><strong>In transit:</strong> All data transmitted between users and the Service is encrypted using TLS 1.2 or higher. Insecure protocols (HTTP, FTP, Telnet) are prohibited.</li>
        <li><strong>At rest:</strong> All data stored in Azure Cosmos DB and Azure Blob Storage is encrypted at rest using platform-managed encryption keys.</li>
      </ul>

      <h2>4. Authentication and Access Control</h2>
      <ul>
        <li><strong>Identity provider:</strong> Microsoft Entra External ID (enterprise-grade CIAM)</li>
        <li><strong>Multi-factor authentication:</strong> Required for privileged and administrative accounts</li>
        <li><strong>Role-based access control (RBAC):</strong> Users are assigned roles that determine which features, sites, and data they can access. The principle of least privilege is enforced.</li>
        <li><strong>Site-level data isolation:</strong> Users assigned to specific sites can only access data for those sites. Supplier competitors&rsquo; data is maintained in complete separation.</li>
        <li><strong>Individual accounts:</strong> No shared credentials. Every user has a unique identity with individual audit trail attribution.</li>
      </ul>

      <h2>5. Audit Trail and Monitoring</h2>
      <p>
        The Service maintains a comprehensive, immutable audit trail that records all data creation, modification, and deletion events. Each audit record includes the identity of the user, the timestamp, and the nature of the change. Audit records are available for client review upon request.
      </p>

      <h2>6. Data Protection</h2>
      <ul>
        <li><strong>Data classification:</strong> All client data is classified as Confidential and handled accordingly.</li>
        <li><strong>Data isolation:</strong> Client data is logically separated. Data from one client engagement is never accessible to users from another.</li>
        <li><strong>Backup and recovery:</strong> Azure Cosmos DB provides continuous 7-day backup with point-in-time restore. Azure Blob Storage uses geo-redundant replication.</li>
        <li><strong>Data retention:</strong> Data is retained per the terms of the applicable client engagement. Upon request or contract termination, client data is securely deleted within 30 calendar days.</li>
      </ul>

      <h2>7. AI and Automated Processing Security</h2>
      <ul>
        <li><strong>Enterprise AI platform:</strong> AI processing uses an enterprise-grade platform with a data processing agreement in place.</li>
        <li><strong>No model training:</strong> Client data is never used to train, fine-tune, or improve AI models.</li>
        <li><strong>Data minimization:</strong> Only the data elements necessary for a specific analytical task are transmitted to AI systems.</li>
        <li><strong>Human oversight:</strong> All AI-generated outputs are reviewed by experienced TMG personnel before finalization.</li>
        <li><strong>Secure connections:</strong> All AI processing occurs over encrypted API connections (HTTPS/TLS 1.2+).</li>
      </ul>

      <h2>8. Security Incident Response</h2>
      <p>
        In the event of a confirmed security incident affecting client data, TMG will notify affected clients within 24&ndash;72 hours of confirmation, provide details of the incident and remediation steps, and comply with all applicable breach notification laws.
      </p>
      <p>
        <strong>To report a security concern,</strong> please contact us using the information in Section 11 below.
      </p>

      <h2>9. Compliance Alignment</h2>
      <p>TMG&rsquo;s security practices are designed to meet or exceed the requirements of:</p>
      <ul>
        <li>GM Third Party Information Security Requirements (TPISR)</li>
        <li>Toyota Third Party Cyber Risk Management standards</li>
        <li>Applicable United States federal and state data protection laws</li>
        <li>Automotive industry best practices for supply chain data management</li>
      </ul>

      <h2>10. Responsible Disclosure</h2>
      <p>
        If you believe you have discovered a security vulnerability in the ToolSight platform, we encourage responsible disclosure. Please report it to us using the information in Section 11 below, with a description of the issue and steps to reproduce it. We ask that you:
      </p>
      <ul>
        <li>Allow us reasonable time to investigate and address the issue before public disclosure</li>
        <li>Avoid accessing or modifying data that does not belong to you</li>
        <li>Act in good faith to avoid disruption to the Service or its users</li>
      </ul>
      <p>
        We appreciate the security research community&rsquo;s efforts to help keep the ToolSight platform and its users safe.
      </p>

      <h2>11. Contact</h2>
      <p>
        For all security-related inquiries &mdash; including responsible disclosure reports, requests for additional security documentation (such as TMG&rsquo;s Information Security Policy), and questions about this Security Overview &mdash; please contact:
      </p>
      <p>
        <strong>Contact</strong><br />
        Tooling Management Group LLC<br />
        Attn: Chris Ongena, CEO<br />
        745 State Circle<br />
        Ann Arbor, MI 48108, USA<br />
        Email: <a href="mailto:chris@toolingmanagementgroup.com">chris@toolingmanagementgroup.com</a>
      </p>
      <p>
        <em>If you are an active user of the Service, your TMG account representative is also available for day-to-day questions.</em>
      </p>
    </LegalPage>
  );
}
